/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  SingleValueParameter,
  MultiValueParameter,
  ParameterBase,
  MultiplicityType,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { OLAPResult } from "@sa/rep-olap";
import { SelectionMode, AggregationType, DataType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { clone, equal, uniq } from "../../../../shared";
import { CUBE_ID } from "./constants";
import { DictionariesService, DictionaryItem } from "@sa/rep-mdm"


type Parameters = {
  business: number;
  worldwide: string;
  totalcrop: string;
  calendar: string;
  commodities_groups: string[];
  countries: string[];
  commodities: string[];
  attributes: string[];
  units: string[];
};

export class ContextReport1 implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;
  protected _exportParameters = new Map<string, ParameterBase>();
  public olapCurrentYearWorldResult$ = new BehaviorSubject<OLAPResult | null>(null);
  public olapCurrentYearCountriesResult$ = new BehaviorSubject<OLAPResult | null>(null);
  public olapLastYearWorldResult$ = new BehaviorSubject<OLAPResult | null>(null);
  public olapLastYearCountriesResult$ = new BehaviorSubject<OLAPResult | null>(null);

  public sources$ = new BehaviorSubject<string | null>(null);
  public commoditiesCountries$ = new BehaviorSubject<Map<number, number[]>>(new Map());

  protected _parameters: BehaviorSubject<Parameters>;
  public get parameters(): Observable<Parameters> {
    return this._parameters.asObservable();
  }

  public worldwide: string;
  public totalcrop: string;

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    const worldwideParameter = createOptions.dataflowService.getParameter("@worldwide") as SingleValueParameter;
    const totalcropParameter = createOptions.dataflowService.getParameter("@totalcrops") as SingleValueParameter;
    const countriesParameter = createOptions.dataflowService.getParameter("@countries") as MultiValueParameter;
    const calendarParameter = createOptions.dataflowService.getParameter("@calendar") as SingleValueParameter;
    const businessParameter = createOptions.dataflowService.getParameter("@business-units") as SingleValueParameter;
    const commoditiesParameter = createOptions.dataflowService.getParameter("@commodities") as MultiValueParameter;
    const commoditiesGroupsParameter = createOptions.dataflowService.getParameter("@commodities_groups") as MultiValueParameter;
    const attributesParameter = createOptions.dataflowService.getParameter("@attributes") as MultiValueParameter;
    const unitsParameter = createOptions.dataflowService.getParameter("@units") as MultiValueParameter;

    this.worldwide = worldwideParameter.value as string;
    this.totalcrop = totalcropParameter.value as string;

    this._parameters = new BehaviorSubject({
      business: businessParameter.value as number,
      worldwide: worldwideParameter.value as string,
      totalcrop: totalcropParameter.value as string,
      countries: ([]) as string[],
      calendar: calendarParameter.value as string,
      commodities: ([]) as string[],
      commodities_groups: commoditiesGroupsParameter.value as string[],
      attributes: attributesParameter.value as string[],
      units: unitsParameter.value as string[],
    });

    this._parameters
      .pipe(debounceTime(300))
      .subscribe((parameters) => {
        const { calendar, commodities, business } = parameters;

        if (commodities.length !== 0 && Boolean(calendar) && Boolean(business)) {
          this.query(parameters);
        }
      });

      const exportParameters = [
         "@e_countries", "@e_commodities"
      ];
      for (const eParamName of exportParameters) {
        let eParameter = this.options.dataflowService.getParameter(eParamName);
        if (eParameter === undefined) {
          this.options.dataflowService.parameters.add({
            id: eParamName,
            multiplicityType: MultiplicityType.Multiple,
            type: DataType.Number,
            allowNullable: false,
            value: []
          });
          eParameter = this.options.dataflowService.getParameter(eParamName);
        }
        this._exportParameters.set(eParamName, eParameter!);
      }
  }

  async update(command: UpdateCommand): Promise<void> {
    const calendar = command.values.get("@calendar") as string;
    const commodities = command.values.get("@commodities") as string[];
    const business = command.values.get("@business-units") as number;
    const parameters = clone(this._parameters.value);
    if (calendar !== undefined) {
      parameters['calendar'] = calendar;
    }
    if (commodities !== undefined) {
      parameters['commodities'] = commodities;
    }
    if (business !== undefined) {
      parameters['business'] = business;
      parameters['commodities'] = [];
    }

    if (!equal(parameters, this._parameters.value)) {
      this._parameters.next(clone(parameters));
    }
  }

  async query(parameters: Parameters) {
    const { calendar, commodities, business } = parameters;
    this.readyState = ComponentContextReadyState.Loading;
    this.olapCurrentYearWorldResult$.next(null);
    this.olapCurrentYearCountriesResult$.next(null);
    this.olapLastYearWorldResult$.next(null);
    this.olapLastYearCountriesResult$.next(null);
    this.sources$.next(null);
    this.commoditiesCountries$.next(new Map());

    const dictionaryService = this.options.serviceProvider.resolve(ServicesRegister.Dictionary) as DictionariesService;

    const { dataSourcesService } = this.options;
    const sources = await Promise.all(commodities.map(commodity => dataSourcesService.getDataSources(3, business, Number(commodity))));
    this.sources$.next(Array.from(new Set(sources.join(', ').split(', ')).values()).join(', '));

    const dictSlideCountries$ = commodities.map(commodity => (async () => {
      const dict = await dictionaryService.getDictionary({
        id: 'slides-countries-display',
        parameters: {
          'slide': 3,
          'olap_commodity': commodity,
          'business_unit': business
        }
      });
      const items = await dict.getAll();
      return [Number(commodity), items] as [number, DictionaryItem[]];
    })());


    const dictSlideCountries = (await Promise.all(dictSlideCountries$));
    this.commoditiesCountries$.next(new Map(dictSlideCountries.map(keyVal => ([keyVal[0], keyVal[1].map(i => i.getValue<number>('olap_country')!)]))));

    const countriesIds: Number[] = uniq(dictSlideCountries.flatMap(x => x[1]!).map(x => x.getValue<number | null>('olap_country')).filter(Boolean)) as number[];
    const worldCountryId = Number(business) === 2 ? 20000 : 100231; 

    const calendarId = Number(calendar);
    const calendarSelections = [-1, 0].map(x => calendarId + (x * 10000));

    const cubeSlideDef = await dataService.getCubeDefinition("slide_3_4_7");
    const cubeParameters = clone(cubeSlideDef.parameters);
    cubeParameters["p_slide"]!["defaultValue"] = '3';
    cubeParameters["p_commodities"]!["defaultValue"] = commodities.join(',');
    cubeParameters["p_countries"]!["defaultValue"] = countriesIds.concat(worldCountryId).join(',');
    cubeParameters["p_attributes"]!["defaultValue"] = [1].join(',');
    cubeParameters["p_calendar"]!["defaultValue"] = calendarSelections.join(',');
    cubeParameters["p_units"]!["defaultValue"] = [9].join(',');
    cubeParameters["p_business"]!["defaultValue"] = business.toString();
    const cubeSlide = await dataService.getCube({
      id: "slide_3_4_7",
      parameters: cubeParameters,
    });


    const eCountriesParameter = this._exportParameters.get("@e_countries")! as MultiValueParameter;
    eCountriesParameter.value = countriesIds.filter(countryId => countryId !== worldCountryId);
    const eCommoditiesParameter = this._exportParameters.get("@e_commodities")! as MultiValueParameter;
    eCommoditiesParameter.value = commodities;
     
    const olapQuery = ({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "commodities.default",
            selection: commodities,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      filters: {
        showEmpty: false,
        parts: [
          { path: "attributes.default", selection: parameters.attributes, selectionMode: SelectionMode.Include },
          { path: "units.default", selection: parameters.units, selectionMode: SelectionMode.Include },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "countries.default", selection: countriesIds.concat(worldCountryId), selectionMode: SelectionMode.Include },
          { path: "calendar.default", selection: calendarSelections, selectionMode: SelectionMode.Include },
          {
            path: "Measures",
            measures: [
              { id: "country_value", aggregationType: AggregationType.None },
              { id: "change", aggregationType: AggregationType.None },
              { id: "world_total_crops", aggregationType: AggregationType.Max },
              { id: "world_value", aggregationType: AggregationType.Max },
              { id: "share_of_country", aggregationType: AggregationType.Max },
              { id: "share_of", aggregationType: AggregationType.Max },
            ],
          },
        ],
      },
      sourceResolveOptions: {
        sourceId: cubeSlide.id,
      },
    } as any);

    const olapResult$ = cubeSlide.query(olapQuery); 
    let [olapResult] = await Promise.all([olapResult$]);
    delete olapResult.data.aggregatedSelection['calendar.default'];
    const currentYearCountriesResult = clone(olapResult);
    const lastYearCountriesResult = clone(olapResult);
    const currentYearWorldResult = clone(olapResult);
    const lastYearWorldResult = clone(olapResult);

    [currentYearCountriesResult, lastYearCountriesResult].forEach((result, i) => {
      const selection = calendarId - (i * 10000);
      result.data.rows = result.data.rows.filter(row => 
        row.selection['calendar.default'] === selection && row.selection['countries.default'] !== worldCountryId
      );
      result.data.rows.forEach(row => {
        delete row.selection['calendar.default'];
      })
    });

    [currentYearWorldResult, lastYearWorldResult].forEach((result, i) => {
      const selection = calendarId - (i * 10000);
      result.data.rows = result.data.rows.filter(row => 
        row.selection['calendar.default'] === selection && row.selection['countries.default'] === worldCountryId
      );
      result.data.rows.forEach(row => {
        delete row.selection['calendar.default'];
      })
    });
    
    this.olapCurrentYearCountriesResult$.next(currentYearCountriesResult);
    this.olapLastYearCountriesResult$.next(lastYearCountriesResult);
    this.olapCurrentYearWorldResult$.next(currentYearWorldResult);
    this.readyState = ComponentContextReadyState.Loaded;
    this.olapLastYearWorldResult$.next(lastYearWorldResult);
  }
}
